import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack, Text, Table, Button, Modal, TextInput,
  Textarea, Select, Group, Alert, Badge,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconAlertCircle, IconExternalLink, IconSubtask } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { permissionsApi } from '@/api/permissions'
import { StatusBadge } from '@/components/chamados/StatusBadge'
import { PriorityBadge } from '@/components/chamados/PriorityBadge'
import { AssigneeAvatar } from '@/components/chamados/AssigneeAvatar'

interface SubTask {
  id: number
  protocol_number: string
  title: string
  status: string
  priority: string
  target_template_id: number | null
  team_name: string | null
  assignee_user_id: number | null
  assignee_name: string | null
}

interface TeamMember {
  id: number
  full_name: string
  email: string
}

interface Props {
  parentTaskId: number
  tenantId: number
  canCreate?: boolean
}

function CreateSubTaskModal({
  opened, onClose, parentTaskId, tenantId,
}: {
  opened: boolean
  onClose: () => void
  parentTaskId: number
  tenantId: number
}) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [titleError, setTitleError] = useState<string | null>(null)
  const [priority, setPriority] = useState('medium')
  const [description, setDescription] = useState('')
  const [teamId, setTeamId] = useState<string | null>(null)
  const [assigneeId, setAssigneeId] = useState<string | null>(null)

  // Times roteáveis — mesma fonte do "Para quem?" do NovoChamado
  const { data: routableTemplates = [] } = useQuery({
    queryKey: ['permission-templates-routable'],
    queryFn: () => permissionsApi.listTemplates({ routable_only: true }),
    enabled: opened,
  })

  const { data: members = [] } = useQuery<TeamMember[]>({
    queryKey: ['team-members', tenantId, teamId],
    queryFn: async () => {
      const res = await api.get<TeamMember[]>(
        `/v1/tenants/${tenantId}/chamados/teams/${teamId}/members`
      )
      return res.data ?? []
    },
    enabled: opened && !!teamId,
  })

  const mutation = useMutation({
    mutationFn: async (payload: {
      title: string; priority: string; description: string
      target_template_id?: number; assignee_user_id?: number
    }) => {
      await api.post(`/v1/tenants/${tenantId}/workflow/${parentTaskId}/sub-tasks`, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-tasks', tenantId, parentTaskId] })
      notifications.show({ color: 'green', message: 'Sub-tarefa criada com sucesso!' })
      setTitle('')
      setTitleError(null)
      setPriority('medium')
      setDescription('')
      setTeamId(null)
      setAssigneeId(null)
      onClose()
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      notifications.show({
        color: 'red',
        message: typeof detail === 'string' ? detail : 'Não foi possível criar a sub-tarefa.',
      })
    },
  })

  const handleSubmit = () => {
    if (!title.trim()) {
      setTitleError('Título e obrigatório.')
      return
    }
    setTitleError(null)
    mutation.mutate({
      title: title.trim(),
      priority,
      description: description.trim(),
      target_template_id: teamId ? Number(teamId) : undefined,
      assignee_user_id: assigneeId ? Number(assigneeId) : undefined,
    })
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Nova Sub-tarefa</Text>}
      size="md"
    >
      <Stack gap="sm">
        <TextInput
          label="Título"
          required
          value={title}
          onChange={(e) => { setTitle(e.target.value); if (titleError) setTitleError(null) }}
          error={titleError}
        />
        <Select
          label="Prioridade"
          value={priority}
          onChange={(v) => setPriority(v ?? 'medium')}
          data={[
            { value: 'low', label: 'Baixa' },
            { value: 'medium', label: 'Média' },
            { value: 'high', label: 'Alta' },
            { value: 'critical', label: 'Crítica' },
          ]}
        />
        <Textarea
          label="Descrição"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Select
          label="Time / Equipe"
          placeholder="Sem time (fica com o chamado pai)"
          clearable
          value={teamId}
          onChange={(v) => { setTeamId(v); setAssigneeId(null) }}
          data={routableTemplates.map((t) => ({ value: String(t.id), label: t.name }))}
        />
        <Select
          label="Responsável"
          description="membros do time selecionado"
          placeholder={teamId ? 'Selecionar responsável' : 'Escolha o time primeiro'}
          clearable
          disabled={!teamId}
          value={assigneeId}
          onChange={setAssigneeId}
          data={members.map((m) => ({ value: String(m.id), label: m.full_name }))}
        />
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={mutation.isPending}>Cancelar</Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>Criar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export function SubTaskList({ parentTaskId, tenantId, canCreate }: Props) {
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)

  const { data: subTasks = [], isLoading, error } = useQuery<SubTask[]>({
    queryKey: ['sub-tasks', tenantId, parentTaskId],
    // Status "vivo" com o detalhe do pai aberto (spec sub-tarefas 21/07)
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await api.get<SubTask[]>(
        `/v1/tenants/${tenantId}/workflow/${parentTaskId}/sub-tasks`
      )
      return res.data
    },
  })

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          Sub-tarefas {subTasks.length > 0 && `(${subTasks.length})`}
        </Text>
        {canCreate && (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => setModalOpen(true)}
          >
            Nova sub-tarefa
          </Button>
        )}
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" py="xs">
          Não foi possível carregar as sub-tarefas. Tente novamente.
        </Alert>
      )}

      {isLoading && <Text size="sm" c="dimmed" role="status">Carregando...</Text>}

      {!isLoading && subTasks.length === 0 && (
        <Stack align="center" gap="xs" py="md">
          <IconSubtask size={32} color="var(--mantine-color-dimmed)" stroke={1.5} />
          <Text size="sm" c="dimmed">Nenhuma sub-tarefa criada. Adicione sub-tarefas para dividir o trabalho.</Text>
        </Stack>
      )}

      {subTasks.length > 0 && (
        // ScrollContainer: 7 colunas com o protocolo em 24-27 chars nao cabem no
        // painel de detalhe do chamado. Mesmo conserto da Gmud.tsx — tabela larga
        // rola no proprio container em vez de comprimir TODAS as celulas.
        <Table.ScrollContainer minWidth={1000}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Protocolo</Table.Th>
              <Table.Th>Título</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Prioridade</Table.Th>
              <Table.Th>Time</Table.Th>
              <Table.Th>Responsável</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {subTasks.map((st) => (
              <Table.Tr key={st.id}>
                <Table.Td>
                  {/* nowrap: o protocolo tem hifens (CHM-STORE-DEV-2026-00001) e
                      o navegador quebra neles. Com 24-27 chars (spec 2026-07-28)
                      a celula quebrava o identificador em duas linhas. */}
                  <Text size="xs" ff="monospace" style={{ whiteSpace: 'nowrap' }}>
                    {st.protocol_number}
                  </Text>
                </Table.Td>
                <Table.Td fw={500}>{st.title}</Table.Td>
                <Table.Td><StatusBadge status={st.status} size="xs" /></Table.Td>
                <Table.Td><PriorityBadge priority={st.priority} size="xs" /></Table.Td>
                <Table.Td>
                  {st.team_name
                    ? <Badge size="xs" variant="outline">{st.team_name}</Badge>
                    : <Text size="xs" c="dimmed">sem time</Text>}
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <AssigneeAvatar
                      assignee={st.assignee_user_id && st.assignee_name
                        ? { id: st.assignee_user_id, full_name: st.assignee_name }
                        : null}
                      taskRequestId={st.id}
                      tenantId={tenantId}
                      canAssign={!!canCreate}
                      teamTemplateId={st.target_template_id}
                      invalidateKeys={[['sub-tasks', tenantId, parentTaskId]]}
                    />
                    <Text size="xs" c={st.assignee_name ? undefined : 'dimmed'}>
                      {st.assignee_name ?? 'sem atribuição'}
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <ActionButton onClick={() => navigate(`/chamados/${st.id}`)} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </Table.ScrollContainer>
      )}

      <CreateSubTaskModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        parentTaskId={parentTaskId}
        tenantId={tenantId}
      />
    </Stack>
  )
}

function ActionButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      size="xs"
      variant="subtle"
      leftSection={<IconExternalLink size={12} />}
      onClick={onClick}
    >
      Abrir
    </Button>
  )
}
